import { IonIcon } from '@ionic/react';
import { bookmarkOutline, trashOutline } from 'ionicons/icons';
import { useCallback, useState } from 'react';
import NFitHistogramChart from '../components/Chart/nfit-histogram';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { setAnnotation } from '../features/selected-scan/selected-scan-slice';
import { replaceHistory } from '../features/scan-results/scan-result-slice';
import { updateUserAnnotation } from '../services/resource-server/resource-server';
import { formatDateForAnnotation } from '../utilities/display-formats';
import { ReactComponent as SleepIcon } from '../assets/sleep.svg';
import { ReactComponent as StressIcon } from '../assets/stress.svg';

const RISK_COLORS = [
    'rgb(178,61,44)',
    'rgb(178,100,100)',
    'rgb(110,91,90)',
    'rgb(102,102,102)',
    'rgb(79,100,87)',
    'rgb(37,110,69)',
    'rgb(44,140,61)',
];

function RiskBar({ value }: { value: number | null }) {
    return (
        <div className="risk-factor-bar-container">
            {RISK_COLORS.map((color, i) => {
                const active = value !== null && value === i;
                return (
                    <div
                        key={i}
                        className="risk-segment"
                        style={{ backgroundColor: color, opacity: value === null ? 1 : active ? 1 : 0.25 }}
                    />
                );
            })}
        </div>
    );
}

export default function Histogram() {
    const [selfReferencingScore, setSelfReferencingScore] = useState<boolean>(false);
    const [daysBack, setDaysBack] = useState<number>(365);
    const [startDate, setStartDate] = useState<string>(
        new Date(new Date().setDate(new Date().getDate() - 365)).toISOString()
    );

    const dispatch = useAppDispatch();
    const selectedScan = useAppSelector((state) => state.selectedScan.record);
    const [displayScore, setDisplayScore] = useState<number | null>(null);
    const [binColor, setBinColor] = useState<string>('#000000');

    const handleStatsReady = useCallback(() => {}, []);
    const handleMostRecentScore = useCallback((score: number) => setDisplayScore(score), []);
    const handleMostRecentBinColor = useCallback((color: string) => setBinColor(color), []);

    const setStartParameter = (days: number) => {
        setDaysBack(days);
        const newDate = days === -1 ? new Date('1900-01-01') : new Date();
        if (days !== -1) newDate.setDate(new Date().getDate() - days);
        setStartDate(newDate.toISOString());
    };

    const trackEnteredAnnotation = (text: string) => {
        dispatch(setAnnotation(text));
    };

    const saveAnnotation = (text: string) => {
        if (selectedScan !== null) {
            const updatedScan = { ...selectedScan, annotation: text };
            dispatch(replaceHistory(updatedScan));
            const sendData = async () => {
                await updateUserAnnotation(selectedScan.id, text);
            };
            sendData();
        }
    };

    const deleteAnnotation = () => {
        dispatch(setAnnotation(''));
        saveAnnotation('');
    };

    return (
        <>
            <div className="drop-shadow-content-box nFitScores">
                <div className="controls">
                    <button className={daysBack === -1 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(-1)}>all time</button>
                    <button className={daysBack === 365 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(365)}>1y</button>
                    <button className={daysBack === 30 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(30)}>1m</button>
                    <button className={daysBack === 7 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(7)}>1wk</button>
                    <label><input type="checkbox" checked={selfReferencingScore} onChange={() => setSelfReferencingScore(!selfReferencingScore)} /> self-reference</label>
                </div>
                <div className="nFitGraph">
                    <NFitHistogramChart
                        selfReferencingScore={selfReferencingScore}
                        startDate={startDate}
                        onStatsReady={handleStatsReady}
                        onMostRecentScore={handleMostRecentScore}
                        onMostRecentBinColor={handleMostRecentBinColor}
                    />
                </div>
                <div className="manageScores">
                    <div className="overallScore">
                        <span className="overallScoreLabel">nFit score</span>
                        <div className="numericOverallScore" style={{ color: binColor }}>
                            {displayScore !== null ? displayScore.toFixed(2) : ''}
                        </div>
                    </div>
                    <div className="annotationForm">
                        <div className="annotationInfo">
                            <IonIcon icon={bookmarkOutline} size="medium"></IonIcon> Annotation {formatDateForAnnotation(selectedScan?.date)}
                        </div>
                        <div className="annotationEntry">
                            <textarea
                                className="annotation"
                                title="" placeholder=""
                                onChange={event => trackEnteredAnnotation(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && event.shiftKey === false) {
                                        event.preventDefault();
                                        saveAnnotation(selectedScan ? selectedScan.annotation : '');
                                    }
                                }}
                                value={selectedScan ? selectedScan.annotation : ''}
                            ></textarea>
                            <button className="trash" title="Delete" onClick={() => deleteAnnotation()}>
                                <IonIcon icon={trashOutline} size="medium"></IonIcon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="drop-shadow-content-box">
                <div className="risk-factor-row">
                    <SleepIcon className="risk-factor-icon" />
                    <RiskBar value={selectedScan?.riskFactors?.last_nights_sleep ?? null} />
                </div>
                <div className="risk-factor-row">
                    <StressIcon className="risk-factor-icon" />
                    <RiskBar value={selectedScan?.riskFactors?.current_stress ?? null} />
                </div>
            </div>
        </>
    );
}
